import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, AlertTriangle } from 'lucide-react';
import type { Profile, Projeto } from '../../types';
import { configDaEtapa } from '../../src/lib/projetos/etapas';
import type { Progresso } from '../../src/lib/projetos/progresso';
import type { MembroEquipe } from '../../src/lib/projetos/equipe';
import EquipeAvatares from './EquipeAvatares';

interface ProjetoCardProps {
  projeto: Projeto;
  progresso: Progresso;
  /** Gestor + quem tem tarefa no projeto (src/lib/projetos/equipe.ts). */
  equipe: MembroEquipe[];
  users: Pick<Profile, 'id' | 'full_name' | 'avatar_url'>[];
}

const formatarData = (iso?: string | null) =>
  iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : null;

/** Card da lista: o objetivo é bater o olho — etapa, progresso, prazo e dono sem clicar. */
const ProjetoCard: React.FC<ProjetoCardProps> = ({ projeto, progresso, equipe, users }) => {
  const cfg = configDaEtapa(projeto.etapa);
  const prazo = formatarData(projeto.prazo);
  const vencido = !!projeto.prazo && projeto.etapa !== 'concluido' &&
    new Date(`${projeto.prazo.slice(0, 10)}T00:00:00`) < new Date(new Date().toDateString());

  return (
    <Link
      to={`/projetos/${projeto.id}`}
      className="block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-bold text-gray-800 dark:text-white leading-snug">{projeto.nome}</h3>
        <span className={`shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cfg.badge}`}>
          {cfg.rotulo}
        </span>
      </div>

      {projeto.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {projeto.tags.map(tag => (
            <span key={tag} className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1.5">
        <span>{progresso.concluidas}/{progresso.total} tarefas</span>
        <span className="font-semibold text-gray-700 dark:text-gray-200">{progresso.pct}%</span>
      </div>
      <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-2">
        <div className="h-2 rounded-full transition-all duration-700"
          style={{ width: `${Math.max(1, progresso.pct)}%`, background: cfg.cor }} />
      </div>

      <div className="flex items-center justify-between mt-3 text-xs">
        <EquipeAvatares equipe={equipe} users={users} />
        <div className="flex items-center gap-2 shrink-0">
          {progresso.atrasadas > 0 && (
            <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400 font-medium">
              <AlertTriangle size={12} /> {progresso.atrasadas}
            </span>
          )}
          {prazo && (
            <span className={`inline-flex items-center gap-1 ${vencido ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
              <CalendarDays size={12} /> {prazo}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProjetoCard;
